import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckboxQuestion as CheckboxQuestionType } from "./types";
import { QuestionTooltip } from "./QuestionTooltip";
import { OtherTextInput, isOtherValue } from "./OtherTextInput";

interface CheckboxQuestionProps {
  question: CheckboxQuestionType & { maxSelections?: number };
  values: string[];
  otherText?: string;
  onChange: (questionId: string, value: string[] | string) => void;
}

export function CheckboxQuestion({
  question,
  values = [],
  otherText = "",
  onChange
}: CheckboxQuestionProps) {
  const maxSelections = question.maxSelections;
  const exclusiveValues = new Set(
    question.options?.filter(o => o.exclusive).map(o => o.value) ?? []
  );

  const hasOtherOption = question.options?.some(o => isOtherValue(o.value));
  const isOtherSelected = values.includes("other");

  const handleToggle = (optionValue: string) => {
    const option = question.options?.find(o => o.value === optionValue);

    if (option?.exclusive) {
      const newValues = values.includes(optionValue) ? [] : [optionValue];
      onChange(question.id, newValues);
    } else {
      const isCurrentlySelected = values.includes(optionValue);
      const nonExclusiveSelected = values.filter(v => !exclusiveValues.has(v)).length;
      if (!isCurrentlySelected && maxSelections && nonExclusiveSelected >= maxSelections) {
        return;
      }
      const newValues = isCurrentlySelected
        ? values.filter(v => v !== optionValue)
        : [...values.filter(v => !exclusiveValues.has(v)), optionValue];
      onChange(question.id, newValues);
    }
  };

  const nonExclusiveSelected = values.filter(v => !exclusiveValues.has(v)).length;

  return (
    <div className="space-y-8 w-full max-w-4xl mx-auto py-4">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-3">
          {question.title}
          {question.tooltip && <QuestionTooltip tooltip={question.tooltip} />}
        </h2>
        {question.description && <p className="text-muted-foreground">{question.description}</p>}
      </div>

      {maxSelections && (
        <p className="text-xs text-muted-foreground -mt-4">
          Select up to {maxSelections} ({nonExclusiveSelected}/{maxSelections} selected)
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 mt-6">
        {question.options?.map(option => {
          const isSelected = values.includes(option.value);
          const isDisabled =
            !isSelected &&
            !option.exclusive &&
            maxSelections !== undefined &&
            nonExclusiveSelected >= maxSelections;

          return (
            <div key={option.value}>
              <div
                onClick={() => !isDisabled && handleToggle(option.value)}
                className={`option-card flex items-center space-x-3 p-4 rounded-md border transition-all max-w-md py-[8px] ${
                  isDisabled
                    ? 'opacity-40 cursor-not-allowed border-border'
                    : 'border-border hover:border-primary hover:bg-muted/50 cursor-pointer'
                }`}
              >
                <Checkbox
                  id={option.value}
                  checked={isSelected}
                  disabled={isDisabled}
                  className="transition-all"
                  onCheckedChange={() => !isDisabled && handleToggle(option.value)}
                />
                <Label
                  htmlFor={option.value}
                  className={`text-base font-medium ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  {option.label}
                </Label>
              </div>
              {hasOtherOption && isOtherValue(option.value) && isOtherSelected && (
                <OtherTextInput
                  questionId={question.id}
                  value={otherText}
                  onChange={(key, val) => onChange(key, val)}
                  placeholder="Please describe..."
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
